import { useCallback, useEffect, useRef, useState } from "react";

import Maths from "./Maths";
import {
  api,
  type CopieQcm,
  type GrilleQcm as Grille,
  type ItemQcm,
  type ReponseSaisie,
} from "./api";
import { validation } from "./palette";

const DELAI_ENREGISTREMENT = 700; // ms d'inactivité avant écriture

// Le professeur lit une lettre entourée sur la copie et la tape. Deux touches
// disent ce qu'aucune lettre ne dit : la question vue et laissée vide, et la
// case qu'on n'arrive pas à lire.
const TOUCHE_BLANCHE = "0";
const TOUCHE_ILLISIBLE = "?";

type Position = { ligne: number; colonne: number };

/** 0 → A, 1 → B… La colonne 0 porte la version, les suivantes les questions. */
const lettre = (rang: number) => String.fromCharCode(65 + rang);
const place = (touche: string) => touche.toUpperCase().charCodeAt(0) - 65;

/** Ce qu'affiche une cellule de réponse. */
function afficher(valeur: ReponseSaisie | undefined): string {
  if (valeur === undefined) return "";
  if (valeur === "blanche") return "–";
  if (valeur === "illisible") return "?";
  return lettre(valeur);
}

export default function GrilleQcm({ qcmId }: { qcmId: number }) {
  const [grille, setGrille] = useState<Grille | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [position, setPosition] = useState<Position>({ ligne: 0, colonne: 0 });
  const [enregistrement, setEnregistrement] = useState<"repos" | "encours" | "echec">(
    "repos",
  );
  // Dit combien de réponses une correction de version vient de relire, pour
  // que le professeur sache qu'elles n'ont pas été perdues.
  const [relecture, setRelecture] = useState<string | null>(null);

  const enAttente = useRef<Map<number, Record<string, ReponseSaisie | null>>>(new Map());
  const minuteur = useRef<number | undefined>(undefined);
  const cellules = useRef<Map<string, HTMLInputElement>>(new Map());

  useEffect(() => {
    api.lireGrilleQcm(qcmId).then(setGrille).catch((e: Error) => setErreur(e.message));
  }, [qcmId]);

  const items = grille?.items ?? [];
  const copies = grille?.copies ?? [];

  const ecrire = useCallback(async () => {
    const lots = [...enAttente.current.entries()];
    enAttente.current.clear();
    if (lots.length === 0) return;

    setEnregistrement("encours");
    try {
      await Promise.all(
        lots.map(([copieId, reponses]) => api.enregistrerReponses(copieId, reponses)),
      );
      setEnregistrement("repos");
    } catch {
      // Les réponses restent affichées : c'est l'écriture qui a échoué.
      setEnregistrement("echec");
    }
  }, []);

  const programmerEcriture = useCallback(() => {
    window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(ecrire, DELAI_ENREGISTREMENT);
  }, [ecrire]);

  useEffect(() => {
    const avantFermeture = (evenement: BeforeUnloadEvent) => {
      if (enAttente.current.size > 0) evenement.preventDefault();
    };
    window.addEventListener("beforeunload", avantFermeture);
    return () => window.removeEventListener("beforeunload", avantFermeture);
  }, []);

  const saisir = useCallback(
    (ligne: number, item: ItemQcm, valeur: ReponseSaisie | null) => {
      setGrille((precedent) => {
        if (!precedent) return precedent;
        const copies = [...precedent.copies];
        const copie = { ...copies[ligne], reponses: { ...copies[ligne].reponses } };
        if (valeur === null) delete copie.reponses[String(item.id)];
        else copie.reponses[String(item.id)] = valeur;
        copies[ligne] = copie;

        const lot = enAttente.current.get(copie.copie_id) ?? {};
        lot[String(item.id)] = valeur;
        enAttente.current.set(copie.copie_id, lot);

        return { ...precedent, copies };
      });
      programmerEcriture();
    },
    [programmerEcriture],
  );

  const deplacer = useCallback(
    (ligne: number, colonne: number) => {
      let l = ligne;
      let c = colonne;
      // La colonne 0 est la version : on avance le long d'une copie, de sa
      // version jusqu'à sa dernière réponse, puis on passe à l'élève suivant.
      if (c > items.length) {
        c = 0;
        l += 1;
      } else if (c < 0) {
        c = items.length;
        l -= 1;
      }
      if (l < 0 || l >= copies.length) return;

      setPosition({ ligne: l, colonne: c });
      cellules.current.get(`${l}:${c}`)?.focus();
    },
    [items.length, copies.length],
  );

  const changerVersion = useCallback(
    async (ligne: number, version: string | null) => {
      const copie = copies[ligne];
      const ancienne = copie.version;

      // Affichée tout de suite, avant même l'aller-retour : sans cela les
      // cellules de réponse restent inertes le temps de la réponse du
      // serveur, le curseur ne peut pas y entrer, et la lettre suivante
      // retombe dans la colonne de version en l'écrasant.
      setGrille((precedent) => {
        if (!precedent) return precedent;
        const copies = [...precedent.copies];
        copies[ligne] = { ...copies[ligne], version };
        return { ...precedent, copies };
      });

      // La saisie en attente part d'abord : le serveur relit ce qu'il a.
      window.clearTimeout(minuteur.current);
      await ecrire();

      const { relues } = await api.changerVersion(copie.copie_id, version);
      setRelecture(
        relues > 0
          ? `${relues} réponse${relues > 1 ? "s" : ""} relue${relues > 1 ? "s" : ""} sur le sujet ${version}`
          : null,
      );
      // Relire la grille n'a de sens que s'il y avait quelque chose à
      // retraduire : à la première saisie, la ligne est vide.
      if (ancienne !== null) setGrille(await api.lireGrilleQcm(qcmId));
    },
    [copies, ecrire, qcmId],
  );

  const auClavier = (
    evenement: React.KeyboardEvent<HTMLInputElement>,
    ligne: number,
    colonne: number,
  ) => {
    const touche = evenement.key;
    const versions = grille?.qcm.versions ?? [];

    const deplacements: Record<string, [number, number]> = {
      ArrowRight: [0, 1],
      ArrowLeft: [0, -1],
      ArrowDown: [1, 0],
      ArrowUp: [-1, 0],
      Enter: [0, 1],
      Tab: [0, evenement.shiftKey ? -1 : 1],
    };
    if (touche in deplacements) {
      evenement.preventDefault();
      const [dl, dc] = deplacements[touche];
      deplacer(ligne + dl, colonne + dc);
      return;
    }

    // --- La colonne de version -------------------------------------------
    if (colonne === 0) {
      if (/^[a-zA-Z]$/.test(touche) && versions.includes(touche.toUpperCase())) {
        evenement.preventDefault();
        changerVersion(ligne, touche.toUpperCase());
        deplacer(ligne, 1);
        return;
      }
      if (touche === "Backspace" || touche === "Delete") {
        evenement.preventDefault();
        changerVersion(ligne, null);
      }
      return;
    }

    // --- Les colonnes de réponses ----------------------------------------
    const item = items[colonne - 1];
    if (copies[ligne].version === null) return; // rien à interpréter encore

    if (/^[a-zA-Z]$/.test(touche) && place(touche) < item.reponses) {
      evenement.preventDefault();
      saisir(ligne, item, place(touche));
      deplacer(ligne, colonne + 1);
      return;
    }
    if (touche === TOUCHE_BLANCHE || touche === " ") {
      evenement.preventDefault();
      saisir(ligne, item, "blanche");
      deplacer(ligne, colonne + 1);
      return;
    }
    if (touche === TOUCHE_ILLISIBLE) {
      evenement.preventDefault();
      saisir(ligne, item, "illisible");
      deplacer(ligne, colonne + 1);
      return;
    }
    if (touche === "Backspace" || touche === "Delete") {
      evenement.preventDefault();
      saisir(ligne, item, null);
      if (touche === "Backspace") deplacer(ligne, colonne - 1);
    }
  };

  const basculerStatut = async (ligne: number) => {
    const copie = copies[ligne];
    const suivant: CopieQcm["statut"] =
      copie.statut === "presente" ? "absente" : "presente";
    await api.changerStatutQcm(copie.copie_id, suivant);
    setGrille((precedent) => {
      if (!precedent) return precedent;
      const copies = [...precedent.copies];
      copies[ligne] = { ...copies[ligne], statut: suivant };
      return { ...precedent, copies };
    });
  };

  if (erreur) return <p className="erreur">Chargement impossible : {erreur}</p>;
  if (!grille) return <p>Chargement…</p>;

  const presentes = copies.filter((c) => c.statut === "presente");
  const totalSaisi = presentes.reduce(
    (somme, c) => somme + Object.keys(c.reponses).length,
    0,
  );
  const totalAttendu = presentes.length * items.length;
  const sansVersion = presentes.filter((c) => c.version === null).length;

  /** Juste, faux, ou indéterminé tant que la version manque. */
  const jugement = (copie: CopieQcm, item: ItemQcm): "juste" | "faux" | null => {
    const valeur = copie.reponses[String(item.id)];
    if (valeur === undefined || copie.version === null) return null;
    if (typeof valeur !== "number") return "faux"; // blanche ou illisible
    return valeur === grille.qcm.bonnes[copie.version]?.[String(item.id)]
      ? "juste"
      : "faux";
  };

  return (
    <section className="saisie">
      <header className="saisie-entete">
        <div>
          <h2>{grille.qcm.titre}</h2>
          <p className="sous-titre">
            {grille.qcm.classe} · {copies.length} élèves · {items.length} questions ·{" "}
            {grille.qcm.versions.length} versions
          </p>
        </div>
        <div className="avancement">
          <progress value={totalSaisi} max={totalAttendu || 1} />
          <span>
            {totalSaisi} / {totalAttendu} saisies
            {enregistrement === "encours" && " · enregistrement…"}
            {enregistrement === "echec" && (
              <strong className="erreur"> · échec d'enregistrement</strong>
            )}
          </span>
        </div>
      </header>

      {sansVersion > 0 && (
        <p className="aide">
          {sansVersion} copie{sansVersion > 1 ? "s" : ""} sans version relevée. La
          lettre du sujet est imprimée en haut de chaque copie : sans elle, une
          réponse ne désigne rien.
        </p>
      )}
      {relecture && <p className="aide">{relecture}.</p>}

      <div className="question-courante">
        {position.colonne > 0 && items[position.colonne - 1] && (
          <>
            <span className="ref">Question {items[position.colonne - 1].numero}</span>
            <Maths texte={items[position.colonne - 1].enonce} syntaxe="typst" />
          </>
        )}
        {position.colonne === 0 && (
          <span className="ref">Version du sujet portée par la copie</span>
        )}
      </div>

      <div className="grille-defilement">
        <table className="grille">
          <thead>
            <tr>
              <th className="colonne-eleve">Élève</th>
              <th className="entete-version">Sujet</th>
              {items.map((item) => (
                <th key={item.id} className="entete-question" title={item.enonce}>
                  {item.numero}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {copies.map((copie, ligne) => {
              const absente = copie.statut !== "presente";
              return (
                <tr
                  key={copie.copie_id}
                  className={[
                    absente ? "absente" : "",
                    ligne === position.ligne ? "ligne-active" : "",
                  ].join(" ")}
                >
                  <th className="colonne-eleve">
                    <button
                      type="button"
                      onClick={() => basculerStatut(ligne)}
                      title={absente ? "Marquer présent" : "Marquer absent"}
                    >
                      {copie.nom} {copie.prenom}
                    </button>
                  </th>

                  <td className="cellule-version">
                    <input
                      ref={(element) => {
                        const cle = `${ligne}:0`;
                        if (element) cellules.current.set(cle, element);
                        else cellules.current.delete(cle);
                      }}
                      value={copie.version ?? ""}
                      readOnly
                      disabled={absente}
                      className={copie.version ? "version" : "version vide"}
                      onFocus={() => setPosition({ ligne, colonne: 0 })}
                      onKeyDown={(e) => auClavier(e, ligne, 0)}
                      aria-label={`${copie.nom} — version du sujet`}
                    />
                  </td>

                  {items.map((item, rang) => {
                    const valeur = copie.reponses[String(item.id)];
                    const verdict = jugement(copie, item);
                    return (
                      <td key={item.id}>
                        <input
                          ref={(element) => {
                            const cle = `${ligne}:${rang + 1}`;
                            if (element) cellules.current.set(cle, element);
                            else cellules.current.delete(cle);
                          }}
                          value={afficher(valeur)}
                          readOnly
                          disabled={absente}
                          // Inerte mais focalisable tant que la version
                          // manque : un champ désactivé ne prend pas le
                          // curseur, et la frappe irait ailleurs.
                          className={[
                            valeur === undefined ? "vide" : "reponse",
                            copie.version === null ? "sans-version" : "",
                          ].join(" ")}
                          style={{
                            color:
                              verdict === null
                                ? undefined
                                : verdict === "juste"
                                  ? validation.conforme
                                  : validation.ecart,
                          }}
                          onFocus={() => setPosition({ ligne, colonne: rang + 1 })}
                          onKeyDown={(e) => auClavier(e, ligne, rang + 1)}
                          aria-label={`${copie.nom} — question ${item.numero}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="aide">
        Relevez d'abord la <strong>lettre du sujet</strong> dans la colonne « Sujet »,
        puis saisissez les réponses : <kbd>A</kbd> à <kbd>D</kbd> selon ce qui est
        entouré. <kbd>0</kbd> pour une question laissée vide, <kbd>?</kbd> pour une
        case illisible, <kbd>Retour arrière</kbd> pour effacer. La couleur dit juste ou
        faux : vous pouvez annoter la copie en même temps que vous la saisissez.
        Corriger une version <strong>relit</strong> les réponses déjà tapées — elles ne
        sont pas perdues. Cliquer sur un nom bascule présent / absent.
      </p>
    </section>
  );
}
